import "core-js/modules/es6.date.to-iso-string";

/* eslint-disable no-console */

/**
 * Get a logger object with `error()`, `warn`, `info`, `debug` and `log()` functions, which
 * - print to browser console when called in the browser in a dev environment.
 * - do nothing when called in the browser in a non-dev environment (unless the the local-storage has "nw.logging.level.global" set)
 * - print using console.log to the server console when called on the server in development mode.
 * - output JSON to the server console when called on the server in non-development mode.
 *
 * This logger is smart enough to distinguish between server-side/client-side and dev/prod execution.
 *
 * - All output contains a timestamp in the iso format.
 * - All output contains the logger name to simplify filtering using dev-tools or kibana.
 * - When NODE_ENV=debug, we will print simple console.foo messages on the console on both server side and client side.
 * - When NODE_ENV!=debug
 *     - on client-side
 *         - We will never log anything unless overridden using a local-storage flag.
 *     - on server-side
 *         - We will not print logger.debug messages on the server-side unless you change the global log level by calling
 *          `setLoggingLevel()`; that's intended to experiment with turning logging off/on based on (say) cookies.
 *         - All output will be in a JSON format that our logstash can pick up.
 *         - All output will have the right log level field set so that you can filter on that.
 *
 * @example
 * ```
 * import { getLogger } from '@nerdwallet/js-logging';
 *
 * const logger = getLogger('<my-module>'); // this will cause all your log messages to be tagged with <my-module>
 * // Note that logger.(log|debug|error|warn|info) all have the signature as console.log.
 * logger.debug('foo');                                  // [iso time][<my-module>] foo
 * logger.debug('I was called with', { arg1, arg2 });    // [iso time][<my-module>] I was called with >{ arg1 … }
 * logger.error('This here error', err);                 // [iso time][<my-module>] This here error > { message… }
 *
 * // You can also create child loggers for each function in a module (say a logger for each action in redux)
 * const rootLogger = getLogger('actions');
 * const someLongAction = (arg1, arg2, arg3) =>
 *   async (dispatch) => {
 *     const logger = getLogger('someLongAction', rootLogger);
 *     logger.debug('called with', arg1, arg2, arg3);    // [iso time][actions > someLongAction] called with arg1 arg2 arg3
 *     try {
 *       await someThing();
 *     } catch (err) {
 *       logger.error(err);                              // [iso time][actions > someLongAction] >{ object containing error message, stack trace etc.}
 *     }
 *   }
 * ```
 */
import { redact } from '@nerdwallet/redact';
import isInDev from './isInDev';
import isOnServer from './isOnServer'; // I don't know how is this a shadow situation but whatever. here you go ESlint

/* eslint-disable-next-line no-shadow */

export var Level;
/* eslint-disable-next-line no-shadow */

(function (Level) {
  Level["none"] = "none";
  Level["error"] = "error";
  Level["warn"] = "warn";
  Level["info"] = "info";
  Level["log"] = "log";
  Level["debug"] = "debug";
})(Level || (Level = {}));

var LevelPriority;
/**
 * A named Logger object which prefixes each console.log (or error) call with the logger's
 * name, making it easier to trace where a given console message originated from.
 */

(function (LevelPriority) {
  LevelPriority[LevelPriority["none"] = 10] = "none";
  LevelPriority[LevelPriority["error"] = 4] = "error";
  LevelPriority[LevelPriority["warn"] = 3] = "warn";
  LevelPriority[LevelPriority["info"] = 2] = "info";
  LevelPriority[LevelPriority["log"] = 2] = "log";
  LevelPriority[LevelPriority["debug"] = 1] = "debug";
})(LevelPriority || (LevelPriority = {}));

;
/**
 * Get the initial value of globalLogLevel. This code is in a function so that we may unit test it.
 * @private
 */

export var getInitialGlobalLogLevel = function getInitialGlobalLogLevel() {
  var isDev = isInDev();
  var globalLogLevel;

  if (isOnServer()) {
    // On the server
    if (isDev) {
      globalLogLevel = Level.debug; // everything will appear on the server console.
    } else {
      globalLogLevel = Level.log; // everything *other than* logger.debug will appear in the logs (elk).
    }
  } else {
    globalLogLevel = isDev ? Level.debug : Level.none;
  } // if an invalid logging level was specified, hide all output.


  if (!LevelPriority[globalLogLevel]) {
    globalLogLevel = Level.none;
  }

  return globalLogLevel;
};
var globalLogLevel = getInitialGlobalLogLevel();
/**
 * This can be used to set logging programatically (Idea: Set logging level based on app-config).
 * @param level - The global logging level to be set. Once set, everything with a log level equal to
 *              or higher priority than the level supplied here will be logged. Everything else
 *              will be ignored.
 */

export var setLoggingLevel = function setLoggingLevel(level) {
  globalLogLevel = level;
};
/**
 * The current log level.
 */

export var getLoggingLevel = function getLoggingLevel() {
  return globalLogLevel;
};
/**
 * Should this logging statement produce output?
 * @private
 */

export var shouldLog = function shouldLog(level) {
  return LevelPriority[level] >= LevelPriority[globalLogLevel];
};
/**
 * This will log to the console based on the supplied log level, and the global log level.
 * Note that the global log level is initialised according to the NODE_ENV and existence of
 * proces.browser. Once initialised, it can be changed by using setLoggingLevel.
 *
 * - We will always log to the browser console as well as the server console in dev mode (and this is not redacted).
 * - We will log nothing to the browser console in non-dev mode.
 * - We will log some stuff to the server console in non-dev mode (this is redacted).
 * @private
 * @param level - log or error.
 * @param messages - varargs, same as console.log or console.error.
 */

export var logInner = function logInner(level) {
  /* istanbul ignore else (because, *cough cough*… THERE IS NO ELSE) */
  if (shouldLog(level)) {
    var dateString = new Date().toISOString();

    for (var _len = arguments.length, messages = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      messages[_key - 1] = arguments[_key];
    }

    if (isInDev()) {
      var _console;

      // In dev mode, server and browser console get treated the same.
      (_console = console)[level].apply(_console, ["[".concat(dateString, "]")].concat(messages));
    } else {
      // Avoiding an "else if" so that we can ignore else with istanbul.

      /* istanbul ignore else */
      // eslint-disable-next-line no-lonely-if
      if (isOnServer()) {
        // Always log on the server.

        /* This should be reasonably similar to bole's output.
        We ARE missing certain fields like context, pid etc. However I noticed that elk doesn't seem
        to actually parse this output. At any rate this is better than console.foo. We'll optimize
        further once we have tried this out. */
        console[level === Level.error ? Level.error : Level.log](JSON.stringify({
          time: dateString,
          level: level,
          message: redact(undefined, messages) // Always redact server logs.

        }));
      }
    }
  }
};
/**
 * @param loggerName - name of the logger. This will be prefixed to the output.
 * @param parentLogger - if supplied, the new logger will have a logger name which incorporates the parent's name.
 * @example
 * ```
 * import { getLogger } from '@nerdwallet/nwjs-cc-shared/logger';
 * const logger = getLogger('my-awesome-action-creator');
 * ...
 * ...
 * try {
 *  throw('something broke');
 * } catch (err) {
 *  logger.error(err);
 * }
 * ```
 *
 * This will output to the console the following
 * ```
 * [iso time string][my-awesome-action-creator] Error: something broke
 *     at vendor.bundle.blah:3784637643764
 *     at ugirugdj...
 * ```
 */

export var getLogger = function getLogger(loggerName, parentLogger) {
  if (parentLogger) {
    // A child logger!
    return getLogger("".concat(parentLogger.getName(), " > ").concat(loggerName));
  }

  var stub = "[".concat(loggerName, "]");
  return {
    /**
     * @param messages - varargs, same as `console.log()`.
     */
    log: function log() {
      for (var _len2 = arguments.length, messages = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
        messages[_key2] = arguments[_key2];
      }

      return logInner.apply(void 0, [Level.log, stub].concat(messages));
    },

    /**
     * @param messages - varargs, same as `console.log()`.
     */
    error: function error() {
      for (var _len3 = arguments.length, messages = new Array(_len3), _key3 = 0; _key3 < _len3; _key3++) {
        messages[_key3] = arguments[_key3];
      }

      return logInner.apply(void 0, [Level.error, stub].concat(messages));
    },

    /**
     * @param messages - varargs, same as `console.log()`.
     */
    warn: function warn() {
      for (var _len4 = arguments.length, messages = new Array(_len4), _key4 = 0; _key4 < _len4; _key4++) {
        messages[_key4] = arguments[_key4];
      }

      return logInner.apply(void 0, [Level.warn, stub].concat(messages));
    },

    /**
     * @param messages - varargs, same as `console.log()`.
     */
    debug: function debug() {
      for (var _len5 = arguments.length, messages = new Array(_len5), _key5 = 0; _key5 < _len5; _key5++) {
        messages[_key5] = arguments[_key5];
      }

      return logInner.apply(void 0, [Level.debug, stub].concat(messages));
    },

    /**
     * @param messages - varargs, same as `console.log()`.
     */
    info: function info() {
      for (var _len6 = arguments.length, messages = new Array(_len6), _key6 = 0; _key6 < _len6; _key6++) {
        messages[_key6] = arguments[_key6];
      }

      return logInner.apply(void 0, [Level.info, stub].concat(messages));
    },

    /**
     * @returns - name of the logger.
     */
    getName: function getName() {
      return loggerName;
    }
  };
};